import React from 'react';
import { ReportSection, ReportSubsection } from '../framework';
import { CalloutBox, CalculationBreakdown, ComparisonGrid } from '../primitives';
import { SectionProps } from './types';

export const SellOptionSection: React.FC<SectionProps> = ({
  assumptions,
  metrics,
  formatCurrency
}) => {
  const blendedReturn = (assumptions.stockReturns * 0.8) + (assumptions.bondReturns * 0.2);

  return (
    <ReportSection id="sell" title="Option B: Sell & Invest" subtitle="The liquid alternative">
      <p className="text-gray-700 mb-6">
        In this scenario, you sell the property and invest the proceeds in a diversified portfolio.
        This is the simpler path—no tenants, no maintenance calls, no property management drama.
      </p>

      <ReportSubsection title="What You'd Net">
        <p className="text-gray-700 mb-4">
          Selling a home involves transaction costs, typically around 6% of the sale price:
        </p>

        <CalculationBreakdown
          steps={[
            { label: 'Sale Price', value: assumptions.propertyValue },
            { label: `Selling Costs (${assumptions.sellingCosts}%)`, value: assumptions.propertyValue * assumptions.sellingCosts / 100, type: 'subtract' },
            { label: 'Net Proceeds to Invest', value: metrics.saleProceeds, type: 'total' }
          ]}
          formatValue={(v) => typeof v === 'number' ? formatCurrency(v) : v}
        />

        <p className="text-gray-600 text-sm mt-4">
          That {assumptions.sellingCosts}% selling cost (~{formatCurrency(Math.round(assumptions.propertyValue * assumptions.sellingCosts / 100))}) is significant—it's money you lose immediately that would have to be
          recovered through better investment returns.
        </p>
      </ReportSubsection>

      <ReportSubsection title="The Investment Portfolio">
        <p className="text-gray-700 mb-4">
          We model an 80/20 stock/bond allocation—a common "balanced growth" portfolio that provides
          growth potential while maintaining some stability:
        </p>

        <ComparisonGrid
          items={[
            {
              label: 'Stocks (80%)',
              value: `${assumptions.stockReturns}%`,
              sublabel: 'Expected real annual return'
            },
            {
              label: 'Bonds (20%)',
              value: `${assumptions.bondReturns}%`,
              sublabel: 'Expected real annual return',
              highlighted: true
            }
          ]}
        />

        <p className="text-gray-700 mt-4">
          The blended expected return is about {blendedReturn.toFixed(1)}%
          real. You could adjust this—go 100% stocks for higher expected return (and more volatility) or
          60/40 for more stability (and lower expected return).
        </p>
      </ReportSubsection>

      <ReportSubsection title="The Simplicity Premium">
        <p className="text-gray-700 mb-4">
          This option has something the rental doesn't: <strong>simplicity</strong>. With a diversified portfolio:
        </p>

        <ul className="list-disc list-inside text-gray-600 space-y-2 mb-4">
          <li><strong>No tenant risk:</strong> No evictions, no late payments, no damage</li>
          <li><strong>No maintenance:</strong> No 2am plumbing emergencies, no roof repairs</li>
          <li><strong>Full liquidity:</strong> Sell any portion any day (try that with a house)</li>
          <li><strong>Geographic freedom:</strong> Your wealth isn't tied to one city's fortunes</li>
          <li><strong>Zero effort:</strong> A diversified index fund requires nothing from you</li>
        </ul>

        <p className="text-gray-700">
          How much is this simplicity worth? That's personal. If you're busy, traveling, or just don't want
          the mental load of being a landlord, it might be worth accepting slightly lower expected returns.
        </p>
      </ReportSubsection>

      <CalloutBox type="warning" title="The Volatility Reality">
        <p>
          Stock markets can drop significantly in a single year—the S&P 500 fell 37% in 2008 and 18% in 2022. While long-term
          returns are strong, you need to be comfortable watching {formatCurrency(Math.round(metrics.saleProceeds))} become {formatCurrency(Math.round(metrics.saleProceeds * 0.65))} (at least temporarily)
          without panicking and selling. If that would keep you up at night, factor that into your decision.
        </p>
      </CalloutBox>

      <ReportSubsection title="How We Get to the Final Number">
        <p className="text-gray-700 mb-4">
          The "Sell & Invest" calculation accounts for taxes along the way and assumes you liquidate
          at year 10 for an apples-to-apples comparison with the rental scenario.
        </p>

        <p className="font-semibold text-gray-800 mb-2">Part 1: Portfolio Growth (After Annual Tax Drag)</p>
        <CalculationBreakdown
          steps={[
            { label: 'Net Sale Proceeds (after 6% selling costs)', value: metrics.saleProceeds },
            { label: `80% in Stocks (${assumptions.stockReturns}% real return)`, value: formatCurrency(metrics.saleProceeds * 0.8), type: 'info' },
            { label: `20% in Bonds (${assumptions.bondReturns}% real return)`, value: formatCurrency(metrics.saleProceeds * 0.2), type: 'info' },
            { label: `Annual Tax Drag (~${((0.013 * assumptions.capitalGainsRate / 100) * 0.8 + (0.04 * assumptions.rentalIncomeTaxRate / 100) * 0.2) * 100 | 0}.${Math.round(((0.013 * assumptions.capitalGainsRate / 100) * 0.8 + (0.04 * assumptions.rentalIncomeTaxRate / 100) * 0.2) * 1000) % 10}% blended)`, value: 'Reduces effective returns', type: 'info' },
            { label: `Effective Blended Return`, value: `~${(blendedReturn - 0.5).toFixed(1)}% after tax drag`, type: 'info' },
            { label: '× Compounded over 10 years', value: `× ${Math.pow(1 + (blendedReturn - 0.5) / 100, 10).toFixed(3)}`, type: 'info' },
            { label: 'Gross Portfolio Value (Year 10)', value: metrics.year10SellGrossValue, type: 'subtotal' }
          ]}
          formatValue={(v) => typeof v === 'number' ? formatCurrency(v) : String(v)}
        />

        <CalloutBox type="info" title="What Is Tax Drag?">
          <p className="mb-2">
            In a taxable account, you pay taxes on investment income each year, even if you don't sell:
          </p>
          <ul className="list-disc list-inside space-y-1 text-sm">
            <li><strong>Dividend taxes:</strong> S&P 500 yields ~1.3% in dividends, taxed at {assumptions.capitalGainsRate}% → ~0.3% annual drag</li>
            <li><strong>Bond interest taxes:</strong> Bonds yield ~4%, taxed as ordinary income at ~{assumptions.rentalIncomeTaxRate}% → ~1.2% annual drag on the bond portion</li>
          </ul>
          <p className="mt-2">
            The blended drag on an 80/20 portfolio is roughly 0.4-0.5% per year. Over 10 years, this adds up to
            ~{formatCurrency(Math.round(metrics.saleProceeds * 0.005 * 10))} in taxes paid along the way.
          </p>
        </CalloutBox>

        <p className="font-semibold text-gray-800 mt-6 mb-2">Part 2: Exit Taxes (If Fully Liquidated)</p>
        <p className="text-gray-700 mb-4">
          For a fair comparison with the rental scenario, we assume you sell the entire portfolio at year 10.
          You'd owe capital gains tax on the growth.
        </p>

        <CalculationBreakdown
          steps={[
            { label: 'Gross Portfolio Value', value: metrics.year10SellGrossValue },
            { label: 'Cost Basis (original sale proceeds)', value: metrics.saleProceeds, type: 'info' },
            { label: 'Taxable Gain', value: metrics.year10SellGrossValue - metrics.saleProceeds, type: 'info' },
            { label: `Capital Gains Tax (~${assumptions.capitalGainsRate}%)`, value: (metrics.year10SellGrossValue - metrics.saleProceeds) * assumptions.capitalGainsRate / 100, type: 'subtract' },
            { label: 'Net Liquidation Value', value: metrics.year10SellValue, type: 'total' }
          ]}
          formatValue={(v) => typeof v === 'number' ? formatCurrency(v) : String(v)}
        />

        <CalloutBox type="warning" title="Important: Tax Flexibility Advantage">
          <p className="mb-2">
            This calculation assumes you sell everything at once. In reality, a stock portfolio gives you
            significant <strong>tax flexibility</strong> that a house doesn't:
          </p>
          <ul className="list-disc list-inside space-y-1 text-sm">
            <li><strong>Sell gradually:</strong> Spread sales over multiple years to stay in lower tax brackets</li>
            <li><strong>Tax-loss harvest:</strong> Sell losers to offset gains, reducing tax bill</li>
            <li><strong>Donate appreciated shares:</strong> Give to charity without paying capital gains</li>
            <li><strong>Hold until death:</strong> Heirs get stepped-up basis (gains never taxed)</li>
            <li><strong>Borrow against it:</strong> Access cash via margin loan without triggering gains</li>
          </ul>
          <p className="mt-2">
            A savvy investor might pay an effective rate of 10-20% on gains over their lifetime, not the full {assumptions.capitalGainsRate}%.
            This flexibility could be worth <strong>$100K+ in tax savings</strong> that isn't captured in these numbers.
          </p>
        </CalloutBox>
      </ReportSubsection>

      <div className="bg-gray-100 p-6 mt-8">
        <h4 className="font-bold text-gray-900 text-lg mb-2">10-Year Outcome: Sell & Invest</h4>
        <p className="text-3xl font-bold text-gray-900">{formatCurrency(metrics.year10SellValue)}</p>
        <p className="text-sm text-gray-600 mt-1">
          Net liquidation value: what you'd have after selling entire portfolio and paying capital gains
        </p>
        <p className="text-xs text-gray-500 mt-2">
          Gross portfolio {formatCurrency(metrics.year10SellGrossValue)} − capital gains tax {formatCurrency(metrics.year10SellGrossValue - metrics.year10SellValue)}
        </p>
      </div>
    </ReportSection>
  );
};
